import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Empty, Spin, Modal } from "antd";
import { ExclamationCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import Header from "../../../component/menu/Header";
import SideMenu from "../../../component/menu/SideMenu";
import TableHeaderComponent from "../../../component/TableHeaderComponent";
import { constants } from "../../../constant/constants";
import InventoryTable from "../../../component/inventory/InventoryTable";
import ApiService from "../../../utils/ApiService";
import useUserStore from "../../../stores/store";
import { ROLES } from "../../../constant/constants";
import InventoryConfirmModal from "../../../component/inventory/InventoryConfirmModal";
import { getInventoryListTableData } from "../../../services/inventory/inventory.service";
import CustomLoader from "../../../component/menu/CustomLoader";

export default function Ingredients() {
  const [modal, contextHolder] = Modal.useModal();
  const { user, restaurantId, showNotification, role, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [ingredients, setIngredients] = useState();
  const [ingredientDeleteId, setIngredientDeleteId] = useState("");
  const [openDelModal, setOpenDelModal] = useState(false);

  function onSearchText(value) {
    if (value === "") {
      getIngredientList({ restaurantId: restaurantId });
      return;
    }
    setSearchText(value);
    const params = {
      restaurantId: restaurantId,
      searchKey: value.trim(),
    };
    getIngredientList(params);
  }

  function onHandlePageChange(page, pageSize) {
    setPage(page);
    setPerPage(pageSize);
    const params = {
      restaurantId: restaurantId,
      searchKey: searchText.trim(),
      page: page,
      perPage: pageSize,
    };
    getIngredientList(params);
  }

  function onHandleEditItem(_id) {
    navigate(`/inventory/ingredients/${_id}/edit`);
  }

  async function onHandleDeleteItem(_id) {
    if (role === ROLES.STAFF) {
      showNotification("You are not authorize to delete this ingredient.");
      return;
    }
    setOpenDelModal(true);
    setIngredientDeleteId(_id);
  }

  async function getIngredientList(params) {
    setListLoading(true);
    if (!params) return;
    if (!params.hasOwnProperty("restaurantId")) {
      console.error("restaurantId is required for getting ingredient list.");
      return;
    }
    try {
      const res = await ApiService.GET_INVENTORY_INGREDIENT_LIST(params, user.token.token);
      if (res.code !== 200) {
        showNotification(res.data.message);
        setListLoading(false);
        return;
      }
      const { payload } = res;
      const { page, perPage, total, records } = payload;
      setPage(page);
      setPerPage(perPage);
      setTotal(total);
      setIngredients(getInventoryListTableData(records, ["restaurantId"]));
      setListLoading(false);
    } catch (err) {
      setListLoading(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  async function deleteIngredient(ingredientId) {
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_INGREDIENT_DELETE(ingredientId, user.token.token);
      if (res.code !== 200) {
        setOpenDelModal(false);
        setNetworkCall(false);
        showNotification(res.data.message);
        return;
      }

      const { payload } = res;
      const { success, dependentFoods, dependentAddOns } = payload;
      if (!success) {
        setOpenDelModal(false);
        setNetworkCall(false);
        IngredientDeleteConfirmationModal(
          "This ingredient is currently being used in a recipe. Please remove it from the recipe first",
          dependentFoods,
          dependentAddOns,
          () => {},
          () => {}
        );
        return;
      }

      setOpenDelModal(false);
      setNetworkCall(false);
      getIngredientList({ restaurantId: restaurantId, searchText: searchText.trim() });
      showNotification("Ingredient delete successfully.", false);
      return;
    } catch (err) {
      setOpenDelModal(false);
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  useEffect(() => {
    getIngredientList({ restaurantId: restaurantId });
  }, [restaurantId]);

  function deleteIngredientAfterConfirmation() {
    deleteIngredient(ingredientDeleteId);
    setOpenDelModal(false);
    setIngredientDeleteId("");
  }

  function closeConfirmationModal() {
    setIngredientDeleteId("");
    setOpenDelModal(false);
  }

  function onHandleClearSearchBtn() {
    setSearchText("");
  }

  function IngredientDeleteConfirmationModal(
    _title,
    dependentFoods,
    dependentAddOns,
    payload,
    handleConfirm = () => {},
    handleCancel = () => {}
  ) {
    return modal.confirm({
      title: <span className="text-md font-p_medium">{_title}</span>,
      icon: <ExclamationCircleOutlined style={{ color: "red", height: "30px", width: "30px" }} />,
      content: (
        <div className="my-3">
          {Array.isArray(dependentFoods) && dependentFoods.length > 0 && (
            <div>
              <h1 className="text-sm text-black font-p_semibold py-2">Foods</h1>
              {dependentFoods.map((food) => (
                <div
                  key={food.id}
                  className="w-full flex items-center justify-between py-2 px-3
               border border-[1px] border-[#FCBD10] rounded-lg mb-1.5 cursor-pointer font-medium"
                   onClick={() => navigate(`/inventory/recipe/${food.id}/edit?recipeType=FOOD`)}
                >
                  <p className="max-w-[calc(100%-50px)] break-words">{food.name}</p>
                  <RightCircleOutlined />
                </div>
              ))}
            </div>
          )}
          {Array.isArray(dependentAddOns) && dependentAddOns.length > 0 && (
            <div>
              <h1 className="text-sm text-black font-p_semibold">Add-Ons</h1>
              {dependentAddOns.map((addOn) => (
                <div
                  key={addOn.id}
                  className="w-full flex items-center justify-between py-2 px-3
               border border-[1px] border-[#FCBD10] rounded-lg mb-1.5 cursor-pointer font-medium"
                  onClick={() => navigate(`/inventory/recipe/${addOn.id}/edit?recipeType=ADD_ONS`)}
                >
                  <p className="max-w-[calc(100%-50px)] break-words">{addOn.name}</p>
                  <RightCircleOutlined />
                </div>
              ))}
            </div>
          )}
        </div>
      ),
      okText: "Confirm",
      cancelText: "Close",
      okButtonProps: {
        style: { backgroundColor: "black", color: "white", fontWeight: 700, display: 'none' },
      },
      cancelButtonProps: {
        style: { backgroundColor: "#FCBD10", color: "black", fontWeight: 700, display: 'none' },
      },
      width: 600,
      closable: true,
      // onOk: () => handleConfirm(payload),
      // onCancel: handleCancel,
    });
  }

  return (
    <div className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chains`} menu={constants.MENU.INVENTORY} currentMenuKey={"T_7_S_7_L_1"} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.INVENTORY} />
        <div className="flex flex-row p-[24px] w-full min-h-[90vh] mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <TableHeaderComponent
              pageRoute={"/inventory/ingredients/add"}
              searchText={searchText}
              setSearchText={setSearchText}
              isDefaultSortByNeeded={false}
              record={{
                title: "Ingredient List",
                hint: "Search in ingredients",
                search: onSearchText,
                onClearSearchText: onHandleClearSearchBtn,
              }}
            />
            <div>
              {listLoading ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : Array.isArray(ingredients) && ingredients.length < 1 ? (
                <div className="h-[50vh] flex items-center justify-center">
                  <Empty />
                </div>
              ) : (
                <div>
                  <InventoryTable
                    data={ingredients}
                    coloredCols={["SN", "Item Name"]}
                    showEditButton={true}
                    handleEdit={onHandleEditItem}
                    handleDelete={onHandleDeleteItem}
                    paginationRecords={{
                      handlePageChange: onHandlePageChange,
                      page: page,
                      perPage: perPage,
                      setPerPage: setPerPage,
                      total: total,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <InventoryConfirmModal
        open={openDelModal}
        title="Are you sure?"
        message="This will remove the ingredient permanently."
        onConfirm={deleteIngredientAfterConfirmation}
        onCancel={closeConfirmationModal}
      />
      {contextHolder}
    </div>
  );
}
